import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { Fade, Grid, InputLabel, Icon, Card, CircularProgress } from '@mui/material';
import SinDatos from 'components/Carteles/SinDatos';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftTypography from 'components/SoftTypography';
import { MessageManager } from 'context';
import { UserContext } from 'context/user';
import { useContext, useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import ProductCardConsumoInterno from './ProductCardConsumoInterno';
import logo from "assets/images/comedor.png";
import { getUproColor } from 'utils/colors';
import logo_alt from "assets/images/comedor_alt.png";

const productoInicial = {
    id: undefined,
    nombre: "",
    precioCarta: "",
    precioEditado: null,
    comentario: "",
    ingredientes: [],
    estado: "Pendiente",
    cantidad: 1,
};

const categoriaInicial = {
    categoria: "",
};

const FADE_TIMEOUT = 400;
const CATEGORIAS_POR_PAGINA = 6;

function AgregarProductoConsumoInterno({ pedido, setPedido, disabled = false, loadingSave = false }) {
    const [producto, setProducto] = useState(productoInicial);
    const [productosOptions, setProductosOptions] = useState([]);
    const [categoria, setCategoria] = useState(categoriaInicial);
    const [showCategorias, setShowCategorias] = useState(true);
    const [busqueda, setBusqueda] = useState("");
    const [debouncedBusqueda, setDebouncedBusqueda] = useState("");
    const { handleSnackbar } = useContext(MessageManager);
    const { user } = useContext(UserContext);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const categoriasContainerRef = useRef(null);
    const [productosVisibles, setProductosVisibles] = useState(4);
    const productosContainerRef = useRef(null);
    const [categoriasVisibles, setCategoriasVisibles] = useState(CATEGORIAS_POR_PAGINA);
    const [disponibilidadOperativaMap, setDisponibilidadOperativaMap] = useState({});
    const idSucursalStock = Number(pedido?.idSede || 1);

    const [getProductos, { data: productos, loading: loadingProductos }] = useLazyQuery(
        gql`
            query productos($filter: JSON, $search: String) {
              productos(filter: $filter, search: $search, order: { field: "nombre", order: ASC }) {
                id
                nombre
                precioCarta
                precioOnline
                precioPorMayor
                precioPorMayorOnline
                esElaborado
                cantidadPorMayor
                categoriaProducto {
                  categoria
                }
                tipoProducto {
                  id
                  tipo
                }
                files {
                  file
                }
                ingredientes {
                  id
                  materiaPrima {
                    nombre
                    stock {
                      cantidad
                    }
                  }
                }
                seccion
                menuDelDia {
                  precio
                }
              }
            }
          `,
        {
            fetchPolicy: "network-only",
            nextFetchPolicy: "cache-first",
            variables: {
                filter: { esIngrediente: false },
            }
        }
    );

    const { data: stockSucursal } = useQuery(
        gql`
                query getProductos($filter: JSON) {
                    materiasPrimas(filter: $filter) {
                        id
                        nombre
                        idProductoReferencia
                        stock {
                            cantidad
                        }
                    }
                }
            `,
        {
            fetchPolicy: "network-only",
            nextFetchPolicy: "cache-first",
            variables: {
                filter: { tipo: "Plato" },
            },
        }
    );

    const [validarDisponibilidadProductos] = useLazyQuery(
        gql`
            query validarDisponibilidadProductos(
                $productos: [DisponibilidadProductoBatchInput!]!
                $idSucursal: ID
            ) {
                validarDisponibilidadProductos(productos: $productos, idSucursal: $idSucursal) {
                    idProducto
                    cantidad
                    ok
                }
            }
        `,
        {
            fetchPolicy: "no-cache",
        }
    );

    const { data: dataCategoria, loading: loadingCategorias } = useQuery(
        gql`
                query categoriasProducto {
                    categoriasProducto(order: { field: "categoria", order: ASC }) {
                        id
                        categoria
                        categoriaPermanente
                        file
                    }
                }
            `
    );

    const categoriasFiltradas = useMemo(() =>
        dataCategoria?.categoriasProducto?.filter((cat) => cat.id != "16" && cat.id != "19" && cat?.categoriaPermanente === null) || [],
        [dataCategoria]
    );

    const mostrarProductos = !!debouncedBusqueda || (categoria && categoria.id);

    const handleVolverCategorias = useCallback(() => {
        setTimeout(() => {
            setCategoria(categoriaInicial);
            setProducto(productoInicial);
            setShowCategorias(true);
        }, FADE_TIMEOUT);
    }, []);

    const handleSearch = (e) => {
        setBusqueda(e.target.value);
        const newTimeout = setTimeout(() => {
            setDebouncedBusqueda(e.target.value);
        }, 700);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        setSearchTimeout(newTimeout);
    };

    const handleScrollCategorias = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
            setCategoriasVisibles((prev) =>
                prev + CATEGORIAS_POR_PAGINA > categoriasFiltradas.length
                    ? categoriasFiltradas.length
                    : prev + CATEGORIAS_POR_PAGINA
            );
        }
    }, [categoriasFiltradas.length]);

    const handleCategoriaClick = useCallback((cat) => {
        setShowCategorias(false);
        setTimeout(() => {
            setCategoria(cat);
            setProducto(productoInicial);
        }, FADE_TIMEOUT);
    }, []);

    const handleScrollProductos = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
            setProductosVisibles((prev) =>
                prev + 4 > productosOptions.length ? productosOptions.length : prev + 4
            );
        }
    }, [productosOptions.length]);

    const handleAddDetalle = useCallback((productoArg) => {
        try {
            const prod = productoArg || producto;
            let cantidad = Number(prod?.cantidad) ?? 0;

            if (!prod?.nombre || !cantidad) {
                handleSnackbar("Debe seleccionar un producto y cantidad válida.", "error");
                return;
            }

            let pedidoActual = pedido || { detalles: [] };
            let productoExistente = (pedidoActual.detalles || []).findIndex(
                (detalle) =>
                    detalle?.producto?.id === prod.id &&
                    detalle?.precioEditado === prod?.precioEditado &&
                    detalle?.estado === "PENDIENTE"
            );

            if (productoExistente !== -1) {
                const updatedDetalles = (pedidoActual.detalles || []).map((detalle, index) => {
                    if (index === productoExistente) {
                        return {
                            ...detalle,
                            precio: prod?.precioCarta,
                            precioEditado: prod?.precioEditado ?? null,
                            cantidad: Number(detalle.cantidad) + cantidad,
                        };
                    }
                    return detalle;
                });
                setPedido({
                    ...pedidoActual,
                    detalles: updatedDetalles,
                });
                const mensaje =
                    cantidad > 1
                        ? `Se agregaron ${cantidad} unidades del producto ${prod.nombre}`
                        : `Se agregó una unidad del producto ${prod.nombre}`;
                handleSnackbar(mensaje, "success");
                setProducto(productoInicial);
                return;
            }

            let nuevoProducto = {
                cantidad: cantidad,
                estado: "PENDIENTE",
                precio: prod.precioCarta,
                precioEditado: prod?.precioEditado ?? null,
                producto: {
                    nombre: prod.nombre,
                    id: prod.id,
                    cantidadPorMayor: prod.cantidadPorMayor,
                    combo: prod.combo,
                    esElaborado: prod.esElaborado,
                    precioCarta: prod.precioCarta,
                    precioOnline: prod.precioOnline,
                    precioPorMayor: prod.precioPorMayor,
                    precioPorMayorOnline: prod.precioPorMayorOnline,
                    seccion: prod.seccion
                },
                comentario: prod.comentario,
            };
            let newPedidoDetail = {
                ...pedidoActual,
                detalles: [...(pedidoActual.detalles || []), nuevoProducto],
            };
            setPedido(newPedidoDetail);
            const mensaje =
                cantidad > 1
                    ? `Se agregaron ${cantidad} unidades del producto ${prod.nombre}`
                    : `Se agregó una unidad del producto ${prod.nombre}`;
            handleSnackbar(mensaje, "success");
            setProducto(productoInicial);
        } catch (error) {
            handleSnackbar("Ocurrió un error al agregar el producto.", "error");
        }
    }, [producto, pedido, setPedido, handleSnackbar]);


    useEffect(() => {
        setProducto(productoInicial);
        setProductosOptions([]);
    }, []);

    const productosConStock = useMemo(() => {
        const productosDisponibles = productos?.productos || [];
        const materiasPrimas = stockSucursal?.materiasPrimas || [];
        const detalles = pedido?.detalles || [];

        const debeMostrarProductos =
            (categoria?.id !== categoriaInicial?.id && !debouncedBusqueda) || !!debouncedBusqueda;

        if (!debeMostrarProductos) {
            return [];
        }

        return productosDisponibles.reduce((acumulador, producto) => {
            let stock = null;
            let cantidad = 0;

            const detalleExistente = detalles.find(
                (detalle) => detalle.producto?.id === producto.id && detalle.estado === "PENDIENTE"
            );
            const cantidadDetalle = detalleExistente ? detalleExistente.cantidad : 0;

            if (producto?.esElaborado) {
                stock = materiasPrimas.find(s => Number(s.idProductoReferencia) === Number(producto.id));
                cantidad = stock?.stock?.cantidad ?? 0;
            } else {
                cantidad = producto.ingredientes?.[0]?.materiaPrima?.stock?.cantidad ?? 0;
            }

            const disponibleOperativo = disponibilidadOperativaMap[Number(producto.id)];
            const sinStockOperativo = disponibleOperativo === false;
            acumulador.push({
                ...producto,
                stockCantidad: cantidad,
                cantidadDetalle,
                disabledStock: sinStockOperativo || cantidadDetalle >= cantidad || cantidad === 0
            });

            return acumulador;
        }, []);
    }, [productos, stockSucursal, debouncedBusqueda, categoria, pedido?.detalles, disponibilidadOperativaMap]);

    useEffect(() => {
        setProductosOptions(productosConStock);
    }, [productosConStock]);

    useEffect(() => {
        const vars = {
            filter: { disponibilidad: 1, idCategoria: debouncedBusqueda ? categoriasFiltradas.map(cat => Number(cat?.id)) : categoria?.id },
            ...(debouncedBusqueda ? { search: debouncedBusqueda } : {})
        };
        getProductos({ variables: vars });
    }, [categoria, debouncedBusqueda, getProductos]);

    useEffect(() => {
        const debeMostrarProductos =
            (categoria?.id !== categoriaInicial?.id && !debouncedBusqueda) || !!debouncedBusqueda;

        const productosDisponibles = productos?.productos || [];
        if (!debeMostrarProductos || productosDisponibles.length === 0) {
            setDisponibilidadOperativaMap({});
            return;
        }

        const pendientesPorProducto = (pedido?.detalles || []).reduce((acc, detalle) => {
            if (detalle?.estado !== "PENDIENTE") return acc;
            const idProd = Number(detalle?.producto?.id || detalle?.idProducto);
            if (!idProd) return acc;
            acc[idProd] = (acc[idProd] || 0) + Number(detalle?.cantidad || 0);
            return acc;
        }, {});

        const productosBatch = productosDisponibles.map((prod) => {
            const idProd = Number(prod.id);
            const cantidadPendiente = Number(pendientesPorProducto[idProd] || 0);
            return {
                idProducto: idProd,
                cantidad: cantidadPendiente + 1,
                esElaborado: !!prod.esElaborado,
            };
        });

        let isActive = true;
        validarDisponibilidadProductos({
            variables: {
                productos: productosBatch,
                idSucursal: idSucursalStock,
            },
        })
            .then(({ data }) => {
                if (!isActive) return;
                const map = {};
                for (const item of data?.validarDisponibilidadProductos || []) {
                    map[Number(item.idProducto)] = !!item.ok;
                }
                setDisponibilidadOperativaMap(map);
            })
            .catch(() => {
                if (!isActive) return;
                setDisponibilidadOperativaMap({});
            });

        return () => {
            isActive = false;
        };
    }, [
        productos,
        pedido?.detalles,
        categoria?.id,
        debouncedBusqueda,
        idSucursalStock,
        validarDisponibilidadProductos,
    ]);

    useEffect(() => {
        if (mostrarProductos && disabled) { handleVolverCategorias(); handleSearch({ target: { value: "" } }) }
    }, [disabled]);

    return (
        <SoftBox display="flex" flexDirection="column">
            <InputLabel htmlFor="busqueda-producto">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Buscar producto
                </SoftTypography>
            </InputLabel>
            <SoftInput
                placeholder={`EJ: Coca Cola`}
                value={busqueda}
                onChange={handleSearch}
                sx={{ width: "100%" }}
            />
            {mostrarProductos && (
                <Fade in={mostrarProductos} timeout={FADE_TIMEOUT} unmountOnExit>
                    <SoftBox my={2}>
                        <SoftButton
                            color="secondary"
                            size="small"
                            circular
                            onClick={() => { handleVolverCategorias(); handleSearch({ target: { value: "" } }) }}
                            sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                        >
                            <Icon sx={{ fontSize: 18 }}>arrow_back</Icon>
                            Volver a categorías
                        </SoftButton>
                        <InputLabel htmlFor="producto">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                {busqueda && categoria?.categoria
                                    ? `Productos de "${busqueda}" y categoría ${categoria.categoria}`
                                    : busqueda
                                        ? `Productos de "${busqueda}"`
                                        : categoria?.categoria
                                            ? `Productos de ${categoria.categoria}`
                                            : ""}
                            </SoftTypography>
                        </InputLabel>
                        <div
                            ref={productosContainerRef}
                            style={{
                                maxHeight: "400px",
                                overflowY: "auto",
                                overflowX: "hidden",
                                marginBottom: 16,
                                borderRadius: 8,
                                border: "1px solid #eee",
                                padding: 8,
                            }}
                            onScroll={handleScrollProductos}
                        >
                            <Grid container spacing={3} py={1}>
                                {loadingProductos ? (
                                    <Grid item xs={12}>
                                        <Fade
                                            in={loadingProductos}
                                            unmountOnExit
                                            mountOnEnter
                                            timeout={{ enter: 600, exit: 400 }}
                                            style={{ transitionTimingFunction: "ease-in-out" }}
                                        >
                                            <SoftBox
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                height="100%"
                                                minHeight="50vh"
                                            >
                                                <SoftBox
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    flexDirection="column"
                                                >
                                                    <SoftBox
                                                        component="img"
                                                        src={logo}
                                                        alt="UPrO Logo"
                                                        width="10rem"
                                                        p={3}
                                                        sx={{
                                                            animation: "blinking 1.5s infinite",
                                                            "@keyframes blinking": {
                                                                "0%": {
                                                                    opacity: 1,
                                                                },
                                                                "50%": {
                                                                    opacity: 0.5,
                                                                },
                                                                "100%": {
                                                                    opacity: 1,
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </SoftBox>
                                            </SoftBox>
                                        </Fade>
                                    </Grid>
                                ) : productosOptions.length > 0 ? (
                                    productosOptions.slice(0, productosVisibles).map((prod) => (
                                        <Grid item xs={12} sm={6} md={4} key={prod.id}>
                                            <ProductCardConsumoInterno
                                                prod={prod}
                                                isSelected={producto?.id === prod.id}
                                                handleUpdate={(item) => {
                                                    setProducto(item);
                                                    setProductosOptions(prev =>
                                                        prev.map(p =>
                                                            p.id === item.id ? { ...p, ...item } : p
                                                        )
                                                    );
                                                }}
                                                handleAddDetalle={handleAddDetalle}
                                                detalles={pedido?.detalles || []}
                                            />
                                        </Grid>
                                    ))
                                ) : (
                                    <Grid item xs={12}>
                                        <SinDatos mensaje="No hay productos disponibles en esta categoría." />
                                    </Grid>
                                )}
                            </Grid>
                        </div>
                    </SoftBox>
                </Fade>
            )}

            {!debouncedBusqueda && (
                <Fade in={showCategorias} timeout={FADE_TIMEOUT} unmountOnExit>
                    <SoftBox my={2}>
                        <InputLabel htmlFor="categoria">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Categoria
                            </SoftTypography>
                        </InputLabel>
                        <div
                            ref={categoriasContainerRef}
                            style={{
                                maxHeight: "400px",
                                overflowY: "auto",
                                overflowX: "hidden",
                                marginBottom: 16,
                                borderRadius: 8,
                                border: "1px solid #eee",
                                padding: 8,
                            }}
                            onScroll={handleScrollCategorias}
                        >
                            <Grid container spacing={3} py={1}>
                                {loadingCategorias ? (
                                    <Grid item xs={12}>
                                        <SoftBox display="flex" justifyContent="center" alignItems="center">
                                            <SoftBox
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                flexDirection="column"
                                                height="100%"
                                            >
                                                <SoftBox component="img" src={logo_alt} alt="UPrO Logo" width="15rem" p={3} />
                                                <SoftTypography variant="h4" align="center" color="white">
                                                    Estamos cargando las categorias
                                                </SoftTypography>
                                                <SoftBox
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    flexDirection="column"
                                                    gap={2}
                                                >
                                                    <SoftTypography variant="h5" align="center" color="white">
                                                        Por favor espera
                                                    </SoftTypography>
                                                    <CircularProgress size={30} color="white" />
                                                </SoftBox>
                                            </SoftBox>
                                        </SoftBox>
                                    </Grid>
                                ) : categoriasFiltradas?.length > 0 ? categoriasFiltradas.slice(0, categoriasVisibles).map((cat) => (
                                    <Grid item xs={12} sm={6} md={4} key={cat.id}>
                                        <Card
                                            sx={{
                                                borderRadius: 3,
                                                overflow: "hidden",
                                                border: "1px solid rgba(0, 0, 0, 0.12)",
                                                minWidth: 0,
                                                cursor: disabled ? "not-allowed" : "pointer",
                                                backgroundColor: disabled
                                                    ? "rgba(0, 0, 0, 0.04)"
                                                    : categoria?.id === cat.id
                                                        ? getUproColor("uproPink")
                                                        : "auto",
                                                boxShadow: categoria?.id === cat.id ? 4 : 1,
                                                transition: "transform 0.2s, box-shadow 0.2s",
                                                "&:hover": {
                                                    transform: disabled ? "none" : "scale(1.03)",
                                                    boxShadow: disabled ? 1 : 6,
                                                },
                                                display: "flex",
                                                flexDirection: "column",
                                                height: "100%",
                                                opacity: disabled ? 0.7 : 1,
                                            }}
                                            onClick={disabled ? () => { } : () => handleCategoriaClick(cat)}
                                        >
                                            <SoftBox
                                                sx={{
                                                    position: "relative",
                                                    width: "100%",
                                                    paddingBottom: "60%",
                                                    overflow: "hidden",
                                                    backgroundColor: "#f5f5f5",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    opacity: disabled ? 0.7 : 1,
                                                }}
                                            >
                                                <SoftBox
                                                    component="img"
                                                    loading="lazy"
                                                    src={cat?.file ? resolveStoredFileUrl(cat?.file) : logo}
                                                    alt={cat.categoria}
                                                    sx={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        opacity: disabled ? 0.15 : 1,
                                                        zIndex: 1,
                                                    }}
                                                />
                                            </SoftBox>
                                            <SoftBox
                                                display="flex"
                                                flexDirection="column"
                                                justifyContent="center"
                                                alignItems="flex-start"
                                                px={2}
                                                py={2}
                                                sx={{ minHeight: 60 }}
                                            >
                                                <SoftTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    color={disabled ? "text.disabled" : "#222"}
                                                    sx={{
                                                        width: "100%",
                                                        textAlign: "left",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    {cat.categoria}
                                                </SoftTypography>
                                            </SoftBox>
                                        </Card>
                                    </Grid>
                                )) : (
                                    <Grid item xs={12}>
                                        <SinDatos mensaje="No hay categorias disponibles" />
                                    </Grid>
                                )}
                            </Grid>
                        </div>
                    </SoftBox>
                </Fade>
            )}
        </SoftBox>
    );
}

export default memo(AgregarProductoConsumoInterno);
